import { Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import validator from '../../onboarding/common/validator';
import { useTranslation } from 'react-i18next';
import { appEnvironment } from '../../common/environment';

export default function PasswordStrengthView(props: Props) {
  const { t } = useTranslation();
  const { password } = props;

  const [passwordStrength, setPasswordStrength] = useState(0);

  const [passwordLength, setPasswordLength] = useState(false);
  const [
    passwordHasUppercaseAndLowercase,
    setPasswordHasUppercaseAndLowercase,
  ] = useState(false);
  const [passwordHasDigit, setPasswordHasDigit] = useState(false);
  const [passwordHasSpecialCharacters, setPasswordHasSpecialCharacters] =
    useState(false);

  useEffect(() => {
    let strength = 0;

    if (password.length > 0) {
      strength++;
    }

    if (validator.passwordLength(password)) {
      strength++;
      setPasswordLength(true);
    } else {
      setPasswordLength(false);
    }

    if (validator.passwordHasUppercaseAndLowercase(password)) {
      strength++;
      setPasswordHasUppercaseAndLowercase(true);
    } else {
      setPasswordHasUppercaseAndLowercase(false);
    }

    if (validator.passwordHasDigit(password)) {
      setPasswordHasDigit(true);
      strength++;
    } else {
      setPasswordHasDigit(false);
    }

    if (validator.passwordHasSpecialCharacters(password)) {
      strength++;
      setPasswordHasSpecialCharacters(true);
    } else {
      setPasswordHasSpecialCharacters(false);
    }

    setPasswordStrength(strength);
  }, [password]);

  const color = useMemo(() => {
    switch (passwordStrength) {
      case 1:
        return appEnvironment.theme.colours.red;
      case 2:
        return appEnvironment.theme.colours.coral;
      case 3:
        return appEnvironment.theme.colours.yellow;
      case 4:
        return appEnvironment.theme.colours.limeYellow;
      case 5:
        return appEnvironment.theme.colours.mint;
      case 0:
      default:
        return appEnvironment.theme.colours.greyBg;
    }
  }, [passwordStrength]);

  const getBarColor = useCallback(
    (index: number) => {
      if (index < passwordStrength) {
        return color;
      }
      return 'white';
    },
    [color, passwordStrength]
  );

  const renderRequirement = useCallback((text: string, valid: boolean) => {
    return (
      <div className="flex items-center gap-[10px]">
        {/* Circle Indicator */}
        <div
          className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full transition-colors duration-300"
          style={{
            backgroundColor: valid
              ? appEnvironment.theme.colours.mint
              : '#D0D0D0',
          }}
        >
          {valid && (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>

        {/* Text Label */}
        <span
          className={`text-[14px] transition-colors duration-300 ${
            valid ? 'text-mint' : 'text-[#9E9E9E]'
          }`}
        >
          {text}
        </span>
      </div>
    );
  }, []);

  return (
    <Stack gap="16px" className="mx-4 mt-4">
      {/* Strength Bars */}
      <div className="flex gap-[6px]">
        {[0, 1, 2, 3, 4].map((index) => (
          <div
            key={index}
            className="h-1 flex-1 rounded-md transition-colors duration-300"
            style={{ backgroundColor: getBarColor(index) }}
          />
        ))}
      </div>

      {/* Requirements List */}
      <div className="flex flex-col gap-[10px]">
        {renderRequirement(t('atLeast8Characters'), passwordLength)}
        {renderRequirement(
          t('bothUppercaseAndLowercase'),
          passwordHasUppercaseAndLowercase
        )}
        {renderRequirement(t('mixtureOfLettersAndNumbers'), passwordHasDigit)}
        {renderRequirement(
          t('atLeastOneSpecialCharacter'),
          passwordHasSpecialCharacters
        )}
      </div>
    </Stack>
  );
}

interface Props {
  password: string;
}
